import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/joy';
import { Bar, KPI, Pie, PivotTable, Table, useDates } from 'src/mtska-frontend-app-component';
import { DashboardPageTitle } from 'src/mtska-frontend-modules/modules/_shared/dashboardFilters';
import { useSettingContextInComponent, useSettingsContext } from 'src/mtska-frontend-app-scaffolding';
import { useDashboardFiltersStorageKey } from 'src/mtska-frontend-modules/modules/_shared/dashboardFilters/DashboardFiltersStorageKeyContext';
import {
    expandUnifiedAgentForGraph,
    migrateToSideAgentFilter,
    migrateToUnifiedAgentFilter,
    UNIFIED_AGENT_FILTER_FIELD,
} from 'src/mtska-frontend-modules/modules/_shared/dashboardFilters/agentFilterUtils';
import { useNavigate } from 'react-router-dom';
import { useDataProvider } from 'src/mtska-frontend-data-provider';
import { buildContractsExpiringBarNavigateHandler } from './buildContractsExpiringBarNavigateHandler';
import { resolveConfiguredTableColumns } from './tableColumnResolvers';
import { getTransposedDateFirstOfMonth } from './dashboardDateUtils';
import { useTranslation } from 'react-i18next';

const withDefaultGroupRowClass = (groupClassName) => {
    const normalized = String(groupClassName || '').trim();
    if (normalized === '') return 'report-itemrow';
    const classes = normalized.split(/\s+/);
    return classes.includes('report-itemrow') ? normalized : `${normalized} report-itemrow`;
};

const withRowStyleVariables = (groupStyle) => {
    if (!groupStyle || typeof groupStyle !== 'object') return undefined;
    const normalizedStyle = { ...groupStyle };
    if (normalizedStyle.gap !== undefined && normalizedStyle['--report-itemrow-gap'] === undefined) {
        normalizedStyle['--report-itemrow-gap'] = normalizedStyle.gap;
    }
    return normalizedStyle;
};

const getUserRoles = (user) => user?.data?.roles ?? [];

const graphVisibleForUser = (graph, userRoles) => {
    if (graph.excludedRoles?.length && graph.excludedRoles.some((r) => userRoles.includes(r))) {
        return false;
    }
    if (graph.requiredRoles?.length && !graph.requiredRoles.some((r) => userRoles.includes(r))) {
        return false;
    }
    return true;
};

const defaultFiltersTransformRegistry = {};

const ConfiguredDashboardPage = ({
    page,
    pageTitle,
    dataProviderName,
    defaultFilters,
    pageFiltersConfig,
    filterSetName,
    filterCatalog,
    graphComponentRegistry = {},
    pageKey,
    filtersTransformRegistry = defaultFiltersTransformRegistry,
    showFilterSetLabel = true,
    filterToolbarExtras = null,
    OverviewComponent = null,
    onClearFilters = null,
}) => {
    const { getComparableTimeframe } = useDates();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { item: user } = useDataProvider('auth/token');
    const userRoles = getUserRoles(user);

    const filtersStorageKey = useDashboardFiltersStorageKey(dataProviderName);
    const { filterModel, drawable } = useSettingContextInComponent(filtersStorageKey, defaultFilters);
    const { addFilter, replaceFilters } = useSettingsContext(filtersStorageKey);
    const baseFilters = filterModel ?? defaultFilters ?? { items: [] };
    const agentFilterMigratedRef = useRef(false);

    useEffect(() => {
        agentFilterMigratedRef.current = false;
    }, [page?.id]);

    useEffect(() => {
        if (!drawable || agentFilterMigratedRef.current) {
            return;
        }

        let migrated = null;
        if (page?.unifiedAgentFilter) {
            migrated = migrateToUnifiedAgentFilter(filterModel);
        } else if (page?.sideAgentFilter) {
            migrated = migrateToSideAgentFilter(filterModel, page.sideAgentFilter);
        }

        if (!migrated) {
            return;
        }

        agentFilterMigratedRef.current = true;
        replaceFilters(migrated);
    }, [page?.unifiedAgentFilter, page?.sideAgentFilter, drawable, filterModel, replaceFilters]);

    const withAccepted = filtersTransformRegistry.withAcceptedStatus;
    const acceptedFilters =
        typeof withAccepted === 'function' ? withAccepted(baseFilters) : baseFilters;

    const resolveFilters = (graph) => {
        const baseByRef = graph.filtersRef === 'accepted' ? acceptedFilters : baseFilters;
        const transform = graph.filtersTransform ? filtersTransformRegistry[graph.filtersTransform] : null;
        return transform ? transform(baseByRef) : baseByRef;
    };

    const applyAgentSideFilter = (filters, side) => {
        if (!side || !filters?.items) {
            return filters;
        }

        const stripField = side === 'buyer'
            ? '_related_sellerAgent_fullname_reverse'
            : side === 'seller'
              ? '_related_buyerAgent_fullname_reverse'
              : null;

        if (!stripField) {
            return filters;
        }

        return {
            items: filters.items.filter((item) => item.field !== stripField),
        };
    };

    const resolveCompareFilters = (graph, graphFilters) => {
        if (!graph.compareConfig || !graph.compareConfig.timeframeField) return undefined;
        const timeframeBaseFilters = graph.compareConfig.sourceRef === 'accepted' ? acceptedFilters : graphFilters;
        const compare = getComparableTimeframe(timeframeBaseFilters, graph.compareConfig.timeframeField);
        if (graph.compareConfig.timeline) {
            return {
                ...compare,
                timeline: graph.compareConfig.timeline,
            };
        }
        return compare;
    };

    const buildGraphFilters = (graph) => {
        let f = resolveFilters(graph);
        if (graph.mergeFilterItems?.length) {
            f = { items: [...(f?.items || []), ...graph.mergeFilterItems] };
        }
        if (page?.unifiedAgentFilter) {
            f = expandUnifiedAgentForGraph(f, graph.agentSideFilter);
        } else if (graph.agentSideFilter) {
            f = applyAgentSideFilter(f, graph.agentSideFilter);
        }
        return f;
    };

    const buildPieSliceHandler = (graph) => {
        const field = graph.filterSliceField || graph.handleFilterField;
        if (!field) return undefined;
        const map = graph.sliceLabelToFilterValue && typeof graph.sliceLabelToFilterValue === 'object'
            ? graph.sliceLabelToFilterValue
            : null;
        return (label) => {
            const value = map && Object.prototype.hasOwnProperty.call(map, label) ? map[label] : label;
            addFilter(field, value, graph.filterSliceOperator || 'equals');
        };
    };

    const buildPieClickHandler = (graph) => {
        if (graph.sliceLabelToFilterValue || graph.filterSliceField) {
            return buildPieSliceHandler(graph);
        }

        const multiFields = Array.isArray(graph.handleFilterFields)
            ? graph.handleFilterFields.filter(Boolean)
            : [];
        if (multiFields.length > 0) {
            return (value) => {
                multiFields.forEach((filterField) => addFilter(filterField, value));
            };
        }

        if (graph.handleFilterField) {
            return (value) => addFilter(graph.handleFilterField, value);
        }

        if (page?.unifiedAgentFilter) {
            return (value) => addFilter(UNIFIED_AGENT_FILTER_FIELD, value);
        }

        return undefined;
    };

    const buildBarHandler = (graph, graphFilters) => {
        if (graph.barInteraction === 'navigateContractsExpiring') {
            return buildContractsExpiringBarNavigateHandler({
                navigate,
                baseFilters: graphFilters,
                field: graph.navigateFilterField || '_related_contract_end_at',
            });
        }
        if (graph.handleFilterField) {
            return (value) => addFilter(graph.handleFilterField, value);
        }
        return undefined;
    };

    const renderSectionTitle = (graph, graphIndex) => {
        if (!graph.sectionTitleKey && !graph.sectionTitle) return null;
        const titleText = graph.sectionTitleKey ? t(graph.sectionTitleKey) : graph.sectionTitle;
        const key = `${page.id || pageKey}-section-${graphIndex}`;
        return (
            <Box key={key} className={graph.sectionTitleClassName || 'dashboard-section-title'}>
                <h2>{titleText}</h2>
            </Box>
        );
    };

    const renderAtomicGraph = (graph, graphIndex) => {
        if (!drawable) return null;
        if (!graphVisibleForUser(graph, userRoles)) return null;

        const graphFilters = buildGraphFilters(graph);
        const compareFilters = resolveCompareFilters(graph, graphFilters);
        const sharedKey = `${page.id || pageKey}-graph-${graph.type}-${graph.dataProviderName || graphIndex}-${graphIndex}`;

        if (graph.type === 'sectionTitle') {
            return renderSectionTitle(graph, graphIndex);
        }

        if (graph.type === 'kpi') {
            return (
                <Box key={sharedKey} className={graph.className || ''}>
                    <KPI
                        title={graph.title}
                        dataProviderName={graph.dataProviderName}
                        filters={graphFilters}
                        compareFilters={compareFilters}
                        comparePositiveCondition={graph.comparePositiveCondition}
                        format={graph.format}
                        compactformat={graph.compactformat}
                        decimaldigits={graph.decimaldigits}
                        className={graph.kpiClassName || ''}
                    />
                </Box>
            );
        }

        if (graph.type === 'pie') {
            const pieIsFilterable = Boolean(graph.filterSliceField);
            const handleFilterChange = buildPieClickHandler(graph);
            const pieWrapperClassName = [
                graph.className || '',
                pieIsFilterable ? 'dashboard-pie-filterable' : 'dashboard-pie-static',
            ]
                .filter(Boolean)
                .join(' ');

            return (
                <Box key={sharedKey} className={pieWrapperClassName}>
                    <Pie
                        title={graph.titleKey ? t(graph.titleKey) : graph.title}
                        dataProviderName={graph.dataProviderName}
                        filters={graphFilters}
                        showTotal={graph.showTotal ?? false}
                        format={graph.format}
                        handleFilterChange={handleFilterChange}
                        donutWidth={graph.donutWidth}
                        height={graph.height}
                        monochrome={graph.monochrome}
                        legendPosition={graph.legendPosition}
                    />
                </Box>
            );
        }

        if (graph.type === 'bar') {
            return (
                <Box key={sharedKey} className={graph.className || ''}>
                    <Bar
                        title={graph.titleKey ? t(graph.titleKey) : graph.title}
                        dataProviderName={graph.dataProviderName}
                        filters={graphFilters}
                        direction={graph.direction}
                        format={graph.format}
                        height={graph.height}
                        showTooltip={graph.showTooltip}
                        showDataLabels={graph.showDataLabels ?? true}
                        excludeNullOrZeroValues={graph.excludeNullOrZeroValues ?? false}
                        handleFilterChange={buildBarHandler(graph, graphFilters)}
                    />
                </Box>
            );
        }

        if (graph.type === 'pivot') {
            return (
                <Box key={sharedKey} className={graph.className || ''}>
                    <PivotTable
                        dataProviderName={graph.dataProviderName}
                        filters={graphFilters}
                        compareFilters={compareFilters}
                        rows={graph.rows || []}
                        values={graph.values || []}
                        columns={graph.columns || []}
                        format={graph.format || 'number'}
                    />
                </Box>
            );
        }

        if (graph.type === 'table') {
            const columns = resolveConfiguredTableColumns(graph.columns, t, userRoles);
            let showAllRoute = graph.showAllRoute;
            if (graph.showAllRoutePreset === 'contractExpiringYear') {
                showAllRoute =
                    '/cars/car?status=running&_related_contract_end_at=' +
                    JSON.stringify({
                        from: getTransposedDateFirstOfMonth(0),
                        to: getTransposedDateFirstOfMonth(12),
                    });
            }
            return (
                <Box key={sharedKey} className={graph.className || ''}>
                    <Table
                        title={graph.titleKey ? t(graph.titleKey) : graph.title}
                        dataProviderName={graph.dataProviderName}
                        filters={graphFilters}
                        columns={columns}
                        drawerRouteBase={graph.drawerRouteBase}
                        showAllRoute={showAllRoute}
                        icon={graph.icon}
                    />
                </Box>
            );
        }

        if (graph.type === 'overview' && OverviewComponent) {
            const aggregateClickField = graph.overviewClickFilterField;
            const legalEntityClickHandler =
                aggregateClickField != null
                    ? (params) => (
                          <span
                              role="presentation"
                              onClick={(e) => {
                                  e.stopPropagation();
                                  addFilter(aggregateClickField, params.value, 'equals');
                              }}
                              style={{ cursor: 'pointer', color: 'var(--app-menu-background-flat-text)' }}
                          >
                              {params.value}
                          </span>
                      )
                    : () => {};

            return (
                <Box key={sharedKey} className={graph.className || ''}>
                    <OverviewComponent
                        dataProviderName={graph.dataProviderName}
                        aggregatorLabel={graph.aggregatorLabelKey ? t(graph.aggregatorLabelKey) : graph.aggregatorLabel}
                        aggregateBy={graph.aggregateBy}
                        filters={graphFilters}
                        legalEntityClickHandler={legalEntityClickHandler}
                    />
                </Box>
            );
        }

        return null;
    };

    const renderLegacyComponent = (graph, graphIndex) => {
        if (!graphVisibleForUser(graph, userRoles)) return null;
        const GraphComponent = graphComponentRegistry[graph.componentKey];
        if (!GraphComponent) return null;
        const key = `${page.id || pageKey}-graph-${graph.componentKey}-${graphIndex}`;
        return (
            <Box key={key} className={graph.className || ''}>
                <GraphComponent
                    dataProviderName={dataProviderName}
                    filters={baseFilters}
                    {...(graph.props || {})}
                />
            </Box>
        );
    };

    const renderGraph = (graph, graphIndex) => {
        if (graph.type) {
            return renderAtomicGraph(graph, graphIndex);
        }
        return renderLegacyComponent(graph, graphIndex);
    };

    return (
        <Box className="dashboard-page" key={pageKey}>
            <DashboardPageTitle
                title={pageTitle}
                dataProviderName={dataProviderName}
                defaultFilters={defaultFilters}
                pageFiltersConfig={pageFiltersConfig}
                filterSetName={filterSetName}
                filterCatalog={filterCatalog}
                showFilterSetLabel={showFilterSetLabel}
                filterToolbarExtras={filterToolbarExtras}
                filterVisibilityUser={user}
                onClearFilters={onClearFilters}
            />

            {(page.groups || []).map((group, groupIndex) => (
                <Box
                    key={`${page.id || pageKey}-group-${groupIndex}`}
                    className={withDefaultGroupRowClass(group.className)}
                    style={withRowStyleVariables(group.style)}
                >
                    {(group.graphs || []).map((graph, graphIndex) => renderGraph(graph, graphIndex))}
                </Box>
            ))}
        </Box>
    );
};

export default ConfiguredDashboardPage;
